import logger from './logger';

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const KEY_STORAGE_NAME = '_ek';

async function generateKey() {
  return await window.crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
}

async function exportKey(key) {
  const exported = await window.crypto.subtle.exportKey('raw', key);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
}

async function importKey(keyData) {
  const rawKey = Uint8Array.from(atob(keyData), c => c.charCodeAt(0));
  return await window.crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
}

async function getKey() {
  let keyData = sessionStorage.getItem(KEY_STORAGE_NAME);
  if (!keyData) {
    const key = await generateKey();
    keyData = await exportKey(key);
    sessionStorage.setItem(KEY_STORAGE_NAME, keyData);
    return key;
  }
  return await importKey(keyData);
}

export async function encryptData(data) {
  try {
    const key = await getKey();
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoded = new TextEncoder().encode(JSON.stringify(data));
    const encrypted = await window.crypto.subtle.encrypt({ name: ALGORITHM, iv }, key, encoded);
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    return JSON.stringify({ _encrypted: true, _v: 1, data: btoa(String.fromCharCode(...combined)) });
  } catch (error) {
    logger.error('[Crypto] Encryption failed');
    throw error;
  }
}

export async function decryptData(encryptedString) {
  try {
    const parsed = JSON.parse(encryptedString);
    if (!parsed._encrypted) return parsed;
    const key = await getKey();
    const combined = Uint8Array.from(atob(parsed.data), c => c.charCodeAt(0));
    const iv = combined.slice(0, IV_LENGTH);
    const encrypted = combined.slice(IV_LENGTH);
    const decrypted = await window.crypto.subtle.decrypt({ name: ALGORITHM, iv }, key, encrypted);
    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (error) {
    logger.error('[Crypto] Decryption failed');
    try { return JSON.parse(encryptedString); } catch { return null; }
  }
}
